import React, { useState, useEffect, useMemo } from "react";
import {
  TextField,
  Checkbox,
  Box,
  Typography,
  Autocomplete,
  InputAdornment,
  IconButton,
  Popper,
  Tooltip,
} from "@mui/material";
import { fontStyles } from "components/Common/commonStyles";
import PropTypes from "prop-types";
import {
  getAllTaskTypes,
  getAllStatus,
  searchEmployeeProfile,
} from "../../../services/userServices";

import { CustomTooltip } from "components/RosterConfig/RosterConfigStyle";
import { GridSearchIcon } from "@mui/x-data-grid";

const CustomPopper = (props) => {
  return (
    <Popper
      {...props}
      placement="bottom-start"
      modifiers={[
        {
          name: "preventOverflow",
          enabled: true,
          options: {
            altBoundary: true,
            tether: false,
            rootBoundary: "viewport",
          },
        },
      ]}
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 2, // > modal (1500), > tooltip (1200), > popover (1300)
      }}
    />
  );
};

const CustomBulkOMTHeader = ({
  field,
  type,
  headerTitle,
  onBulkEdit,
  selectedRows,
  setSnack,
  resetValue,
  onTechSolutionDescriptionChange,
  options,
  headerTooltip,
  setTaskTypeId,
}) => {
  const [value, setValue] = useState(type === "boolean" ? false : "");
  const [taskOptions, setTaskOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [selectedTech, setSelectedTech] = useState(null);
  const [selectedBuild, setSelectedBuild] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [asdLeadOracleId, setAsdLeadOracleId] = useState(null);
  const [asdLeadName, setAsdLeadName] = useState("");
  const [searchCriteria, setSearchCriteria] = useState("");

  useEffect(() => {
    let cancelled = false;

    const fetchOptions = async () => {
      try {
        if (field === "TaskTypeDescription") {
          const result = await getAllTaskTypes();

          if (cancelled) return;

          if (!cancelled) {
            setTaskOptions(result?.data?.data || []);
          }
        } else if (
          field === "FinalStatusDescription" ||
          field === "ScheduleStatus" ||
          field === "EXTScheduleStatus" ||
          field === "PERRScheduleStatus" ||
          field === "RecoveryScheduleStatus"
        ) {
          const result = await getAllStatus();

          if (cancelled) return;

          if (!cancelled) {
            setStatusOptions([
              { OMTFinalStatusId: null, FinalStatusDescription: "" },
              ...(result.data?.data || []),
            ]);
          }
        }
      } catch (error) {
        if (!cancelled) {
          setSnack({
            open: true,
            message: `Failed to fetch options for ${field}. Please try again later.`,
            severity: "error",
          });
        }
      }
    };

    fetchOptions();

    return () => {
      cancelled = true;
    };
  }, [resetValue, type, field]);

  useEffect(() => {
    let initialValue;
    if (type === "boolean") {
      initialValue = false;
    } else if (type === "date") {
      initialValue = null;
    } else if (type === "autocomplete") {
      initialValue = null;
    } else if (type === "textfield") {
      initialValue = "";
    } else if (type === "peoplePicker") {
      initialValue = null;
    } else {
      initialValue = "";
    }
    setValue(initialValue);
    setSearchResults([]);

    if (type === "peoplePicker") {
      setAsdLeadOracleId(null);
      setAsdLeadName("");
      setSearchCriteria("");
    }

    if (type === "autocomplete") {
      setSelectedTech(null);
      setSelectedBuild(null);
    }
  }, [resetValue, type]);

  const handleBulkEdit = (event, value) => {
    if (selectedRows.length === 0) {
      setSnack({
        open: true,
        message: "No rows selected for bulk edit.",
        severity: "error",
      });
      return;
    }
    let newValue;
    if (type === "boolean") {
      newValue = event.target.checked;
    } else if (type === "autocomplete") {
      newValue = value;
    } else if (type === "peoplePicker") {
      newValue = value;
    } else if (type === "textfield") {
      newValue = value;
    } else {
      newValue = event.target.value;
    }

    setValue(newValue);
    onBulkEdit(field, newValue);
  };

  const handleDateTimeChange = (event) => {
    if (selectedRows.length === 0) {
      setSnack({
        open: true,
        message: "No rows selected for bulk edit.",
        severity: "error",
      });
      return;
    }

    const newValue = event.target.value === "" ? null : event.target.value;
    setValue(newValue);
    onBulkEdit(field, newValue);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Tab") {
      event.preventDefault();
      const isShift = event.shiftKey;
      const focusableElements = document.querySelectorAll('[tabindex="0"]');
      const currentIndex = Array.from(focusableElements).indexOf(event.target);

      let nextIndex = isShift ? currentIndex - 1 : currentIndex + 1;
      if (nextIndex < 0) nextIndex = focusableElements.length - 1;
      if (nextIndex >= focusableElements.length) nextIndex = 0;

      focusableElements[nextIndex]?.focus();
    }
  };

  const safeOptions = useMemo(() => {
    return Array.isArray(options) ? options : [];
  }, [options]);

  useEffect(() => {
    if (
      value &&
      safeOptions.length > 0 &&
      field === "TechSolutionDescription"
    ) {
      if (typeof value === "object" && value.OMTTechSolutionId) {
        setSelectedTech(value);
      } else {
        const match = safeOptions.find(
          (opt) =>
            opt.OMTTechSolutionId === value ||
            opt.OMTTechSolutionId === value?.OMTTechSolutionId,
        );
        setSelectedTech(match ?? null);
      }
    } else if (field === "TechSolutionDescription") {
      setSelectedTech(null);
    }
  }, [value, safeOptions, field]);

  useEffect(() => {
    if (
      value &&
      Array.isArray(options) &&
      options.length > 0 &&
      field === "BuildDescription"
    ) {
      const match = options.find((opt) => opt.OMTBuildId === value?.OMTBuildId);
      setSelectedBuild(match ?? null);
    } else if (field === "BuildDescription") {
      setSelectedBuild(null);
    }
  }, [value, options, field]);

  const handleASDSearch = async (event, searchQuery) => {
    if (searchQuery.length < 3) {
      setSnack({
        open: true,
        message: "Search criteria must be at least 3 characters in length.",
        severity: "error",
      });
      return;
    }
    const searchResult = await searchEmployeeProfile(searchQuery);
    if (searchResult.status === true) {
      let searchResultDetails;

      if (/^\d+$/.test(searchQuery) && searchQuery.length >= 4) {
        searchResultDetails = searchResult.data.map((emp) => ({
          OracleId: emp.OracleId,
          Display:
            (emp.Name ?? "") + (emp.OracleId ? ` (${emp.OracleId})` : ""),
          Name: emp.Name,
          Email: emp.Email,
        }));
      } else {
        searchResultDetails = searchResult.data.map((emp) => ({
          OracleId: emp.OracleId,
          Display: emp.Display,
          Name: emp.Name,
          Email: emp.Email,
        }));
      }

      setSearchResults(searchResultDetails);
      setAsdLeadOracleId(searchResultDetails[0]?.OracleId || null);
      setAsdLeadName(searchResultDetails[0]?.Name || "");
      setSnack({
        open: true,
        message: "We found results matching the search criteria.",
        severity: "success",
      });
    } else {
      setSnack({
        open: true,
        message: "No result found matching with search criateria.",
        severity: "error",
      });
    }
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      flexDirection="column"
      sx={{
        height: "100%",
        gap: 1,
        justifyContent: "center",
      }}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <Tooltip title={headerTooltip || headerTitle} placement="top" arrow>
        <Typography
          sx={{
            fontFamily: "Urbanist, Verdana, Arial, sans-serif",
            fontSize: "12px",
            letterSpacing: "0.10em",
            fontWeight: "bold",
          }}
        >
          {headerTitle}
        </Typography>
      </Tooltip>
      {type === "autocomplete" &&
        field === "TechSolutionDescription" &&
        safeOptions.length > 0 && (
          <Autocomplete
            size="small"
            sx={{
              width: "100%",
              minWidth: "230px",
              lineHeight: "normal !important",
              "&. MuiAutocomplete-endAdornment": {
                top: "50%",
                transform: "translateY(-50%)",
              },
              "& .MuiOutlinedInput-root": {
                padding: "2px 8px",
                minHeight: "40px",
              },
              "& input": {
                lineHeight: "normal !important",
                padding: "6px 8px",
              },
            }}
            options={safeOptions}
            value={selectedTech || null}
            getOptionLabel={(option) => option?.TechSolutionDescription ?? ""}
            isOptionEqualToValue={(option, value) =>
              option.OMTTechSolutionId === value.OMTTechSolutionId
            }
            onChange={async (event, newValue) => {
              if (newValue) {
                setSelectedTech(newValue);
                const hasBuildOptions =
                  await onTechSolutionDescriptionChange(newValue);
                handleBulkEdit(event, {
                  ...newValue,
                  clearBuild: !hasBuildOptions,
                });
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select"
                size="small"
                margin="none"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  sx: {
                    padding: "0px !important",
                  },
                }}
                sx={{
                  margin: 0,
                  padding: 0,
                  lineHeight: "normal !important",
                  "& .MuiInputBase-root": {
                    fontSize: "12px",
                    padding: "2px 8px",
                  },
                  "& input": {
                    padding: "6px 0px",
                  },
                }}
              />
            )}
          />
        )}
      {type === "autocomplete" && field === "BuildDescription" && (
        <Autocomplete
          size="small"
          sx={{
            width: "100%",
            minWidth: "150px",
            marginBottom: "0px !important",
            paddingBottom: "0px !important",
            lineHeight: "normal !important",
            height: "auto",
            "&. MuiAutocomplete-endAdornment": {
              top: "50%",
              transform: "translateY(-50%)",
            },
            "& .MuiOutlinedInput-root": {
              padding: "2px 8px",
              minHeight: "40px",
            },
            "& input": {
              lineHeight: "normal !important",
              padding: "6px 8px",
            },
          }}
          options={options}
          value={selectedBuild || ""}
          getOptionLabel={(option) => option?.BuildDescription ?? ""}
          isOptionEqualToValue={(option, value) =>
            option.OMTBuildId === value.OMTBuildId
          }
          onChange={(e, newValue) => {
            setSelectedBuild(newValue || null);
            handleBulkEdit(e, newValue || null);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select"
              size="small"
              margin="none"
              fullWidth
              InputProps={{
                ...params.InputProps,
                sx: {
                  padding: "0px !important",
                },
              }}
              sx={{
                margin: 0,
                padding: 0,
                lineHeight: "normal !important",
                "& .MuiInputBase-root": {
                  fontSize: "12px",
                  padding: "2px 8px",
                },
                "& input": {
                  padding: "6px 0px",
                },
              }}
            />
          )}
        />
      )}
      {type === "autocomplete" && field === "TaskTypeDescription" && (
        <Autocomplete
          size="small"
          sx={{
            width: "100%",
            minWidth: "190px",
            marginBottom: "0px !important",
            paddingBottom: "0px !important",
            lineHeight: "normal !important",
            height: "auto",
            "&. MuiAutocomplete-endAdornment": {
              top: "50%",
              transform: "translateY(-50%)",
            },
            "& .MuiOutlinedInput-root": {
              padding: "2px 8px",
              minHeight: "40px",
            },
            "& input": {
              lineHeight: "normal !important",
              padding: "6px 8px",
            },
          }}
          options={taskOptions || []}
          value={value}
          getOptionLabel={(option) => option?.TaskTypeDescription || ""}
          isOptionEqualToValue={(option, value) =>
            option.OMTTaskTypeId === value.OMTTaskTypeId
          }
          onChange={(event, newValue) => {
            if (newValue) {
              setValue(newValue);
              onBulkEdit(field, newValue);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select"
              size="small"
              margin="none"
              fullWidth
              InputProps={{
                ...params.InputProps,
                sx: {
                  padding: "0px !important",
                },
              }}
              sx={{
                margin: 0,
                padding: 0,
                lineHeight: "normal !important",
                "& .MuiInputBase-root": {
                  fontSize: "12px",
                  padding: "2px 8px",
                },
                "& input": {
                  padding: "6px 0px",
                },
              }}
            />
          )}
        />
      )}
      {type === "autocomplete" &&
        (field === "FinalStatusDescription" ||
          field === "ScheduleStatus" ||
          field === "EXTScheduleStatus" ||
          field === "PERRScheduleStatus" ||
          field === "RecoveryScheduleStatus") && (
          <Autocomplete
            size="small"
            sx={{
              width: "100%",
              minWidth: "200px",
              marginBottom: "0px !important",
              paddingBottom: "0px !important",
              lineHeight: "normal !important",
              height: "auto",
              "&. MuiAutocomplete-endAdornment": {
                top: "50%",
                transform: "translateY(-50%)",
              },
              "& .MuiOutlinedInput-root": {
                padding: "2px 8px",
                minHeight: "40px",
              },
              "& input": {
                lineHeight: "normal !important",
                padding: "6px 8px",
              },
            }}
            options={statusOptions || []}
            value={value}
            getOptionLabel={(option) => option?.FinalStatusDescription || ""}
            isOptionEqualToValue={(option, value) =>
              option.OMTFinalStatusId === value.OMTFinalStatusId
            }
            renderOption={(props, option) => (
              <li {...props}>
                {option.FinalStatusDescription === "" ? (
                  <em style={{ color: "#aaa" }}>None</em>
                ) : (
                  option.FinalStatusDescription
                )}
              </li>
            )}
            onChange={(event, newValue) => {
              if (newValue) {
                setValue(newValue?.FinalStatusDescription || "");
                handleBulkEdit(event, newValue);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select"
                size="small"
                margin="none"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  sx: {
                    padding: "0px !important",
                  },
                }}
                sx={{
                  margin: 0,
                  padding: 0,
                  lineHeight: "normal !important",
                  "& .MuiInputBase-root": {
                    fontSize: "12px",
                    padding: "2px 8px",
                  },
                  "& input": {
                    padding: "6px 0px",
                  },
                }}
              />
            )}
          />
        )}
      {type === "singleSelect" && field === "asdLead" && (
        <Autocomplete
          onChange={handleBulkEdit}
          size="small"
          sx={{ minWidth: 230 }}
        />
      )}
      {type === "boolean" && (
        <Checkbox
          checked={!!value || false}
          size="medium"
          onChange={handleBulkEdit}
        />
      )}
      {type === "string" && (
        <TextField
          fullWidth
          size="small"
          value={value}
          onChange={handleBulkEdit}
        />
      )}
      {type === "number" && (
        <TextField
          fullWidth
          size="small"
          type="number"
          value={value}
          onChange={handleBulkEdit}
          inputProps={{ min: 0 }}
        />
      )}
      {type === "datetime" && (
        <TextField
          key={resetValue}
          size="small"
          id="datetime-local"
          type="datetime-local"
          value={value}
          sx={{
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "",
              },
            },
          }}
          InputLabelProps={{
            shrink: true,
          }}
          onChange={handleDateTimeChange}
          InputProps={{
            sx: {
              ...fontStyles,
            },
          }}
          fullWidth
        />
      )}
      {type === "peoplePicker" && (
        <Autocomplete
          key={resetValue}
          value={value}
          openOnFocus
          sx={{
            width: "100%",
            minWidth: "225px",
            lineHeight: "normal !important",
            "&. MuiAutocomplete-endAdornment": {
              top: "50%",
              transform: "translateY(-50%)",
            },
            "& .MuiOutlinedInput-root": {
              padding: "2px 8px",
              minHeight: "40px",
            },
            "& input": {
              lineHeight: "normal !important",
              padding: "6px 8px",
            },
            "& .MuiDataGrid-columnHeaders": {
              overflow: "visible !important",
            },
          }}
          options={searchResults}
          onInputChange={(event, newInputValue) => {
            if (newInputValue === "") {
              setAsdLeadOracleId(null);
              setAsdLeadName("");
            }
          }}
          onChange={(event, selected) => {
            if (selected) {
              setAsdLeadOracleId(selected.OracleId);
              setAsdLeadName(selected.Email);
              setValue(selected);
              handleBulkEdit(event, selected);
            } else {
              setValue(null);
              setAsdLeadName("");
            }
          }}
          PopperComponent={CustomPopper}
          getOptionLabel={(option) => option.Display || ""}
          renderInput={(params) => (
            <TextField
              name="asdLead"
              {...params}
              size="small"
              placeholder="Search ASD"
              value={asdLeadName || ""}
              onChange={(event) => setSearchCriteria(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleASDSearch(event, searchCriteria);
                }
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <InputAdornment position="end">
                    <CustomTooltip title="Search">
                      <IconButton
                        id="icon-button-search"
                        type="button"
                        onClick={(event) =>
                          handleASDSearch(event, searchCriteria)
                        }
                      >
                        <GridSearchIcon />
                      </IconButton>
                    </CustomTooltip>
                    {params.InputProps.endAdornment}
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      )}
      {type === "textfield" && (
        <TextField
          key={resetValue}
          size="small"
          placeholder={
            field === "Comments" ? "Enter Comments" : field === "TicketNumber" ? "Enter Ticket number" : "Enter Failure Reason"
          }
          value={value || ""}
          onChange={(event) => {
            const nextValue =
              field === "TicketNumber"
                ? event.target.value.replace(/[^a-zA-Z0-9]/g, "")
                : event.target.value;
            setValue(nextValue);
            handleBulkEdit(event, nextValue);
          }}
          fullWidth
          multiline
          rows={2}
          inputProps={{
            maxLength: field === "Comments" ? 10000 : field === "TicketNumber" ? 50 : 100,
          }}
          helperText={`${(value || "").length}/${field === "Comments" ? 10000 : field === "TicketNumber" ? 50 : 100}`}
          sx={{
            width: "100%",
            minWidth: field === "Comments" ? "400px" : "200px",
            margin: 0,
            padding: 0,
            "& .MuiOutlinedInput-root": {
              padding: "8px",
              fontSize: "12px",
            },
            "& textarea": {
              lineHeight: "normal !important",
              padding: "0px",
            },
          }}
        />
      )}
    </Box>
  );
};

CustomBulkOMTHeader.propTypes = {
  field: PropTypes.string.isRequired,
  type: PropTypes.oneOf([
    "singleSelect",
    "boolean",
    "string",
    "number",
    "date",
    "datetime",
    "autocomplete",
    "peoplePicker",
    "textfield",
  ]).isRequired,
  headerTitle: PropTypes.string.isRequired,
  headerTooltip: PropTypes.string,
  onBulkEdit: PropTypes.func.isRequired,
  selectedRows: PropTypes.array.isRequired,
  setSnack: PropTypes.func.isRequired,
  resetValue: PropTypes.bool.isRequired,
  onTechSolutionDescriptionChange: PropTypes.func,
  options: PropTypes.array,
};

export default CustomBulkOMTHeader;
